// src/actions/idfcActions.js
import axios from 'axios';
import {
  GET_ALL_IDFC_RECORDS_REQUEST,
  GET_ALL_IDFC_RECORDS_SUCCESS,
  GET_ALL_IDFC_RECORDS_FAIL,
  IMPORT_IDFC_RECORDS_REQUEST,
  IMPORT_IDFC_RECORDS_SUCCESS,
  IMPORT_IDFC_RECORDS_FAIL,
  EXPORT_IDFC_RECORDS_REQUEST,
  EXPORT_IDFC_RECORDS_SUCCESS,
  EXPORT_IDFC_RECORDS_FAIL,
  UPDATE_IDFC_RECORD_REQUEST,
  UPDATE_IDFC_RECORD_SUCCESS,
  UPDATE_IDFC_RECORD_FAIL,
  DELETE_IDFC_RECORD_REQUEST,
  DELETE_IDFC_RECORD_SUCCESS,
  DELETE_IDFC_RECORD_FAIL,
  BULK_DELETE_IDFC_RECORDS_REQUEST,
  BULK_DELETE_IDFC_RECORDS_SUCCESS,
  BULK_DELETE_IDFC_RECORDS_FAIL,
} from '../constants/idfcConstant';
import { logout } from './userActions';

const baseUrl = process.env.REACT_APP_API_URL;

// Get all IDFC records with search and pagination
export const getAllIdfcRecords = (queryParams = '') => async (dispatch, getState) => {
  try {
    dispatch({ type: GET_ALL_IDFC_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    // Parse and validate query parameters
    const params = new URLSearchParams(queryParams);
    const page = Math.max(1, parseInt(params.get('page')) || 1);
    const limit = Math.max(1, parseInt(params.get('limit')) || 10);
    const search = params.get('search') || '';
    const importDate = params.get('importDate') || '';
    const status = params.get('status') || '';
    const fosFilter = params.get('fosFilter') || '';

    // Rebuild clean query string
    let cleanQuery = `search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`;
    if (importDate) {
      cleanQuery += `&importDate=${encodeURIComponent(importDate)}`;
    }
    if (status) {
      cleanQuery += `&status=${encodeURIComponent(status)}`;
    }
    if (fosFilter) {
      cleanQuery += `&fosFilter=${encodeURIComponent(fosFilter)}`;
    }

    console.log('IDFC API Call with params:', { search, page, limit, importDate, status, fosFilter });

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.get(`${baseUrl}/idfc?${cleanQuery}`, config);

    dispatch({
      type: GET_ALL_IDFC_RECORDS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: GET_ALL_IDFC_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Import IDFC records from file
export const importIdfcRecords = (formData) => async (dispatch, getState) => {
  try {
    dispatch({ type: IMPORT_IDFC_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(`${baseUrl}/idfc/import`, formData, config);

    dispatch({
      type: IMPORT_IDFC_RECORDS_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: IMPORT_IDFC_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Export IDFC records
export const exportIdfcRecords = () => async (dispatch, getState) => {
  try {
    dispatch({ type: EXPORT_IDFC_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
      responseType: 'blob',
    };

    const { data } = await axios.get(`${baseUrl}/idfc/export`, config);

    // Create download link
    const url = URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'IDFC_Export.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);

    dispatch({
      type: EXPORT_IDFC_RECORDS_SUCCESS,
      payload: 'Export completed successfully',
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: EXPORT_IDFC_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Update IDFC record
export const updateIdfcRecord = (id, recordData) => async (dispatch, getState) => {
  try {
    dispatch({ type: UPDATE_IDFC_RECORD_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.put(`${baseUrl}/idfc/${id}`, recordData, config);

    dispatch({
      type: UPDATE_IDFC_RECORD_SUCCESS,
      payload: data,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: UPDATE_IDFC_RECORD_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Delete IDFC record
export const deleteIdfcRecord = (id) => async (dispatch, getState) => {
  try {
    dispatch({ type: DELETE_IDFC_RECORD_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    await axios.delete(`${baseUrl}/idfc/${id}`, config);

    dispatch({
      type: DELETE_IDFC_RECORD_SUCCESS,
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: DELETE_IDFC_RECORD_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

// Bulk delete IDFC records
export const bulkDeleteIdfcRecords = (ids) => async (dispatch, getState) => {
  try {
    dispatch({ type: BULK_DELETE_IDFC_RECORDS_REQUEST });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo.token}`,
      },
    };

    const { data } = await axios.post(`${baseUrl}/idfc/bulk-delete`, { ids }, config);

    dispatch({
      type: BULK_DELETE_IDFC_RECORDS_SUCCESS,
      payload: { deletedCount: data.deletedCount },
    });
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: BULK_DELETE_IDFC_RECORDS_FAIL,
      payload: error.response && error.response.data.message
        ? error.response.data.message
        : error.message,
    });
  }
};

export const syncIdfcTaskStatus = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'SYNC_IDFC_TASK_STATUS_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    const { data } = await axios.post(
      `${baseUrl}/idfc/sync-status`,
      {},
      config
    );

    dispatch({
      type: 'SYNC_IDFC_TASK_STATUS_SUCCESS',
      payload: data,
    });

    return data; // Return data including updatedRecords
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'SYNC_IDFC_TASK_STATUS_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error;
  }
};

// Get Field Executives
export const getFieldExecutives = (search = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'GET_FIELD_EXECUTIVES_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    const { data } = await axios.get(
      `${baseUrl}/idfc/field-executives?search=${encodeURIComponent(search)}`,
      config
    );

    dispatch({
      type: 'GET_FIELD_EXECUTIVES_SUCCESS',
      payload: data,
    });

    return data; // Return the data
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'GET_FIELD_EXECUTIVES_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error; // Throw error so it can be caught
  }
};

export const getIdfcFOS = (importDate = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: 'GET_FOS_REQUEST',
    });

    const {
      userLogin: { userInfo },
    } = getState();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userInfo?.token}`,
      },
    };

    let url = `${baseUrl}/idfc/fos`;
    if (importDate) {
      url += `?importDate=${encodeURIComponent(importDate)}`;
    }

    const { data } = await axios.get(url, config);

    dispatch({
      type: 'GET_FOS_SUCCESS',
      payload: data,
    });

    return data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
      return;
    }
    dispatch({
      type: 'GET_FOS_FAIL',
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    throw error;
  }
};